import {
  put,
  takeLatest,
  takeEvery,
  select,
  all,
  take,
} from 'redux-saga/effects';
import _ from 'lodash';
import history from 'app/routes/history';
import {
  TeammoApi,
  normalize,
  compilePaginationObject,
  getStoredAdmin,
} from 'app/utils/redux/helpers';

import localStorage from 'local-storage';
import IApplicationState from 'app/types/state';
import { IStoredAdmin } from 'app/types/common';
import { getAdminId } from 'app/ducks/authentication/selectors';

import {
  competitionSchema,
  competitionListSchema,
  teamConnectionListSchema,
} from 'app/utils/redux/schemas';
import { handleError } from 'app/utils/handlers/handleError';
import { loadingStart, loadingFinish } from 'app/ducks/loading/actions';
import {
  sagaActions,
  getAllCompetitions,
  selectCompetition,
} from 'app/ducks/competitions/actions';
import { CompetitionsActionTypes as types } from 'app/ducks/competitions/types';

function* createCompetitionSaga(action): any {
  yield put(sagaActions.createCompetitionPending(action.payload));
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).competitions.create(action.payload);

  if (response) {
    const normalizedResponse = normalize(response, competitionSchema);
    setTimeout(() => {
      history.push(`/competitions/${response.data.id}`);
    }, 1000);
    yield put(sagaActions.createCompetitionSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.createCompetitionFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}
function* resetTeamConnectionsSaga(action): any {
  yield put(sagaActions.resetTeamConnectionsPending(action.payload));
  const [error, response] = yield (
    yield TeammoApi(yield select())
  ).teamConnections.reset(action.payload.data);

  if (response) {
    const normalizedResponse = normalize(response, teamConnectionListSchema);
    yield put(sagaActions.resetTeamConnectionsSuccess(normalizedResponse));


    if (action.payload.callbackFunction !== undefined) {
      action.payload.callbackFunction(
        response.meta,
      );
    }
  } else {
    yield put(
      handleError(
        error,
        sagaActions.resetTeamConnectionsFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}

function* duplicateCompetitionSaga(action): any {
  yield put(sagaActions.duplicateCompetitionPending(action.payload));
  const { id, ...payload } = action.payload;
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).competitions.duplicate(id, payload);

  if (response) {
    const normalizedResponse = normalize(response, competitionSchema);
    setTimeout(() => {
      history.push(`/competitions/${response.data.id}`);
    }, 1000);
    yield put(sagaActions.duplicateCompetitionSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.duplicateCompetitionFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}

function* updateCompetitionSaga(action): any {
  yield put(sagaActions.updateCompetitionPending(action.payload));
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).competitions.update(action.payload);

  if (response) {
    const normalizedResponse = normalize(response, competitionSchema);

    yield put(sagaActions.updateCompetitionSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.updateCompetitionFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}

function* getCompetitionSaga(action): any {
  yield put(sagaActions.getCompetitionPending());
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).competitions.show(action.payload);
  if (response) {
    const normalizedResponse = normalize(response, competitionSchema);
    yield put(sagaActions.getCompetitionSuccess(normalizedResponse));
    return normalizedResponse.entities.competitions[normalizedResponse.result];
  }
  yield put(
    handleError(
      error,
      sagaActions.getCompetitionFailed(
        _.isEmpty(error) ? {} : error.response.data.errors,
      ),
    ),
  );
  return null;
}

function* getAllCompetitionsSaga(action): any {
  yield put(
    sagaActions.getAllCompetitionsPending(
      action.payload.compile ? action.payload.pagination : {},
    ),
  );
  yield put(loadingStart());

  const paginationObject = action.payload.compile
    ? compilePaginationObject(
      yield select((state) => state.competitions.pagination),
    )
    : action.payload.pagination;

  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).competitions.list(paginationObject);

  if (response) {
    const normalizedResponse = normalize(response, competitionListSchema);
    yield put(
      sagaActions.getAllCompetitionsSuccess(normalizedResponse, response.meta),
    );
    yield put(loadingFinish());
  } else {
    yield put(
      error,
      sagaActions.getAllCompetitionsFailed(
        _.isEmpty(error) ? {} : error.response.data.errors,
      ),
    );
  }
}

function* selectLastCompetitionSaga(): any {
  yield put(sagaActions.selectLastCompetitionPending());
  const storedUser: IStoredAdmin = getStoredAdmin();
  // let currentCompetitionId: string = getCurrentCompetitionId(yield select(), storedUser);
  const storedCompetitionId = storedUser.currentCompetitionId;
  const stateCompetitionId = yield select(
    (state: IApplicationState) => state.competitions.currentCompetitionId,
  );
  let currentCompetitionId = storedCompetitionId || stateCompetitionId;
  if (storedCompetitionId === undefined && stateCompetitionId === undefined) {
    yield put(yield getAllCompetitions());
    yield take(types.GET_ALL_COMPETITIONS_SUCCESS);

    const competitions = yield select((state) => state.competitions);
    if (competitions.hasLoaded && competitions.competitionIds.length > 0) {
      currentCompetitionId = _.head(competitions.competitionIds);
    }
  }
  if (_.isEmpty(currentCompetitionId)) {
    history.push('/create-competition');
    yield put(sagaActions.selectLastCompetitionFinished(currentCompetitionId));
    return;
  }
  const competition = yield* getCompetitionSaga({
    payload: currentCompetitionId,
  });
  if (_.isEmpty(competition)) {
    localStorage.set(yield getAdminId(), {});
    yield* selectLastCompetitionSaga();
    return;
  }
  if (competition !== null) {
    yield all([put(selectCompetition(competition.id))]);
  }

  // const currentCompetition = yield select(
  //   (state) => state.competitions.competitionsById[
  //     state.competitions.currentCompetitionId
  //   ],
  // );
  // if (currentCompetitionId !== undefined && _.isEmpty(currentCompetition)) {
  //   yield take(types.GET_COMPETITION_SUCCESS);
  // }
  yield put(sagaActions.selectLastCompetitionFinished(currentCompetitionId));
}

function* selectCompetitionSaga(action): any {
  const competitionId = action.payload.currentCompetitionId;

  localStorage.set(yield getAdminId(), {
    currentCompetitionId: competitionId,
  });
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createCompetitionSagaWatcher(): any {
  yield takeLatest(types.CREATE_COMPETITION_REQUESTED, createCompetitionSaga);
}
function* duplicateCompetitionSagaWatcher(): any {
  yield takeLatest(
    types.DUPLICATE_COMPETITION_REQUESTED,
    duplicateCompetitionSaga,
  );
}
function* resetTeamConnectionsSagaWatcher(): any {
  yield takeLatest(
    types.RESET_TEAM_CONNECTIONS_REQUESTED,
    resetTeamConnectionsSaga,
  );
}

function* updateCompetitionSagaWatcher(): any {
  yield takeLatest(types.UPDATE_COMPETITION_REQUESTED, updateCompetitionSaga);
}

function* getCompetitionSagaWatcher(): any {
  yield takeEvery(types.GET_COMPETITION_REQUESTED, getCompetitionSaga);
}

function* getAllCompetitionsSagaWatcher(): any {
  yield takeLatest(
    types.GET_ALL_COMPETITIONS_REQUESTED,
    getAllCompetitionsSaga,
  );
}

function* selectLastCompetitionSagaWatcher(): any {
  yield takeLatest(
    types.SELECT_LAST_COMPETITION_REQUESTED,
    selectLastCompetitionSaga,
  );
}

function* selectCompetitionSagaWatcher(): any {
  yield takeLatest(types.SELECT_COMPETITION_REQUESTED, selectCompetitionSaga);
}

export default function* root(): any {
  yield all([
    createCompetitionSagaWatcher(),
    resetTeamConnectionsSagaWatcher(),
    duplicateCompetitionSagaWatcher(),
    updateCompetitionSagaWatcher(),
    getCompetitionSagaWatcher(),
    getAllCompetitionsSagaWatcher(),
    selectLastCompetitionSagaWatcher(),
    selectCompetitionSagaWatcher(),
  ]);
}
